// src/pages/ProfilePage.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress
} from '@mui/material';
import {
  Edit,
  Save,
  CameraAlt,
  Visibility,
  VisibilityOff,
  Person,
  Security,
  Notifications,
  Payment,
  Badge,
  Star,
  Task,
  VerifiedUser,
  CreditCard,
  Camera,
  CheckCircle,
  Cancel,
  Upload,
  Description,
  ArrowForward,
  Refresh
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ProfilePage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    notifications: {
      email: true,
      push: true,
      taskAlerts: true,
      promotional: false
    },
    privacy: {
      profileVisible: true,
      earningsVisible: false,
      activityVisible: true
    }
  });

  // Verification state
  const [verificationStatus, setVerificationStatus] = useState('not_started');
  const [verificationStep, setVerificationStep] = useState(0);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [verificationDocuments, setVerificationDocuments] = useState({
    idFront: null,
    idBack: null,
    selfie: null
  });
  const [verificationError, setVerificationError] = useState(null);
  const [verificationSuccess, setVerificationSuccess] = useState(null);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Toggle password visibility
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [category, subField] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [subField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://propertyearn.net/api/api/user/profile', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch profile data');
        
        const data = await response.json();
        setUserData(data);
        setFormData(prev => ({
          ...prev,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || ''
        }));

        // Set verification status based on backend data
        if (data.verification_status) {
          setVerificationStatus(data.verification_status);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Save profile changes
  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        })
      });

      if (!response.ok) throw new Error('Failed to update profile');
      
      setSuccess('Profile updated successfully!');
      setEditMode(false);
      const updatedData = await response.json();
      setUserData(updatedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };


  // Change password
  const handleChangePassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change password');
      }

      setSuccess('Password changed successfully!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle file upload for verification
  const handleFileUpload = (documentType, file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      setVerificationError('File size must be less than 5MB');
      return;
    }

    setVerificationDocuments(prev => ({
      ...prev,
      [documentType]: file
    }));

    // Auto-advance to next step if this isn't the last document
    if (documentType === 'idFront' && verificationStep === 0) {
      setVerificationStep(1);
    } else if (documentType === 'idBack' && verificationStep === 1) {
      setVerificationStep(2);
    }
  };

  // Submit verification documents
  const handleSubmitVerification = async () => {
    setUploading(true);
    setVerificationError(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (verificationDocuments.idFront) {
        formData.append('id_front', verificationDocuments.idFront);
      }
      if (verificationDocuments.idBack) {
        formData.append('id_back', verificationDocuments.idBack);
      }
      if (verificationDocuments.selfie) {
        formData.append('selfie', verificationDocuments.selfie);
      }

      const response = await fetch('https://propertyearn.net/api/api/user/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit verification');
      }

      setVerificationSuccess('Verification submitted successfully!');
      setVerificationStatus('pending');
      setVerificationDialogOpen(false);
      setVerificationStep(0);
      setVerificationDocuments({
        idFront: null,
        idBack: null,
        selfie: null
      });

      // Refresh user data to get updated verification status
      const profileResponse = await fetch('https://propertyearn.net/api/api/user/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (profileResponse.ok) {
        const userData = await profileResponse.json();
        setUserData(userData);
      }
    } catch (err) {
      setVerificationError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Get verification status chip
const getVerificationChip = () => {
  const status = userData?.verification_status || verificationStatus;

  switch (status) {
    case 'approved':
    case 'verified':
      return (
        <Chip
          icon={<VerifiedUser />}
          label="Verified"
          color="success"
          variant="filled"
          sx={{ mb: 1 }}
        />
      );

    case 'pending':
      return (
        <Chip
          icon={<CircularProgress size={16} />}
          label="Under Review"
          color="warning"
          variant="outlined"
          sx={{ mb: 1 }}
        />
      );

    case 'rejected':
      return (
        <Chip
          icon={<Cancel sx={{ color: '#c62828' }} />} // darker red icon
          label="Verification Failed"
          variant="outlined"
          sx={{
            mb: 1,
            borderColor: '#ef5350',
            color: '#b71c1c',
            backgroundColor: '#ffebee',
            fontWeight: 500
          }}
        />
      );

    default:
      return (
        <Chip
          icon={<Badge sx={{ color: '#ad1457' }} />} // reddish accent
          label="Not Verified"
          variant="outlined"
          sx={{
            mb: 1,
            borderColor: '#f48fb1',
            color: '#880e4f',
            backgroundColor: '#fce4ec',
            fontWeight: 500
          }}
        />
      );
  }
};


  // Add this function to check verification status periodically
      const checkVerificationStatus = async (showNotification = true) => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('https://propertyearn.net/api/api/user/profile', {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Cache-Control': 'no-cache' // Prevent caching
            }
          });

          if (response.ok) {
            const data = await response.json();
            const previousStatus = userData?.verification_status;
            
            setUserData(data);
            setVerificationStatus(data.verification_status);
            
            if (showNotification && previousStatus !== data.verification_status) {
              if (data.verification_status === 'approved') {
                setSuccess('Your account has been verified! 🎉');
              } else if (data.verification_status === 'rejected') {
                setError('Verification was rejected. Please check your documents and try again.');
              }
            }
          }
        } catch (err) {
          console.log('Error checking verification status:', err);
        }
      };


// Add this useEffect for periodic checking
useEffect(() => {
  if (verificationStatus === 'pending') {
    const interval = setInterval(checkVerificationStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }
}, [verificationStatus]);


// Add to your existing state
const [billingData, setBillingData] = useState({
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  phone: '',
  email: '',
  name: ''
});
const [savingBilling, setSavingBilling] = useState(false);

// Add this function to handle billing input changes
const handleBillingInputChange = (field, value) => {
  setBillingData(prev => ({ ...prev, [field]: value }));
};

// Add this function to fetch billing data
    const fetchBillingInfo = async () => {
      setLoadingBilling(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://propertyearn.net/api/api/user/billing', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setBillingData(data);
        }
      } catch (err) {
        console.log('Error fetching billing info:', err);
      } finally {
        setLoadingBilling(false);
      }
    };

//  validation function
const validateBillingData = () => {
  const requiredFields = ['name', 'email', 'address_line1', 'city', 'postal_code', 'country'];
  const missingFields = requiredFields.filter(field => !billingData[field]?.trim());
  
  if (missingFields.length > 0) {
    setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
    return false;
  }
  
  if (billingData.email && !/\S+@\S+\.\S+/.test(billingData.email)) {
    setError('Please enter a valid email address');
    return false;
  }
  
  return true;
};

// Update handleUpdateBilling with validation
const handleUpdateBilling = async () => {
  setSavingBilling(true);
  setError(null);
  setSuccess(null);

  try {
    const token = localStorage.getItem('token');
    const response = await fetch('https://propertyearn.net/api/api/user/billing', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(billingData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update billing information');
    }

    setSuccess('Billing information updated successfully!');
    
    // Refresh the billing data from server to show updated values
    await fetchBillingInfo();
    
  } catch (err) {
    setError(err.message);
  } finally {
    setSavingBilling(false);
  }
};

// Add this useEffect to load billing data when the tab is opened
useEffect(() => {
  if (tabValue === 4) { // Payment Methods tab (index 4)
    fetchBillingInfo();
  }
}, [tabValue]);



  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Profile & Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account settings and preferences
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Layout */}
      <Grid 
        container 
        spacing={4} 
        sx={{ flexWrap: "nowrap", alignItems: "flex-start" }}
      >
        {/* Left Column - Profile Summary */}
        <Grid item md={4} sx={{ flexShrink: 0, minWidth: 300 }}>
          <Card sx={{ position: 'sticky', top: 100 }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              {/* Profile Avatar */}
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: '3rem',
                    bgcolor: 'primary.main',
                    mb: 2
                  }}
                  src={userData?.profilePicture}
                >
                  {userData?.name?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                  size="small"
                >
                  <CameraAlt fontSize="small" />
                </IconButton>
              </Box>

              {/* User Info */}
              <Typography variant="h6" gutterBottom>
                {userData?.name || 'User'}
              </Typography>
              
              {/* Verification Status */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              {getVerificationChip()}
              <Chip
                icon={<Badge />}
                label={userData?.tier || 'Basic'}
                color={
                  userData?.tier === 'Pro'
                    ? 'primary'
                    : userData?.tier === 'Verified'
                    ? 'success'
                    : 'default'
                }
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </Box>


              {/* Stats */}
              <Box sx={{ textAlign: 'left', mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <Star fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Rating
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {userData?.rating?.toFixed(1) || '0.0'}/5.0
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <Task fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Tasks Completed
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {userData?.tasks_completed || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    Jan 2024
                  </Typography>
                </Box>
              </Box>

              {/* Verification Button */}
              {verificationStatus === 'not_started' && (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => setVerificationDialogOpen(true)}
                  startIcon={<VerifiedUser />}
                >
                  Verify Account
                </Button>
              )}

              {verificationStatus === 'rejected' && (
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => setVerificationDialogOpen(true)}
                  startIcon={<Upload />}
                >
                  Resubmit Verification
                </Button>
              )}

              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setEditMode(!editMode)}
                startIcon={editMode ? <Save /> : <Edit />}
              >
                {editMode ? 'Save Changes' : 'Edit Profile'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Settings Tabs */}
        <Grid item md sx={{ minWidth: 0 }}>
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <Tabs value={tabValue} onChange={handleTabChange} centered>
              <Tab icon={<Person />} label="Profile" />
              <Tab icon={<Security />} label="Security" />
              <Tab icon={<VerifiedUser />} label="Verification" />
              <Tab icon={<Notifications />} label="Notifications" />
              <Tab icon={<Payment />} label="Payment Methods" />
            </Tabs>

            {/* Profile Tab */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={!editMode}
                    placeholder="+44 123 456 7890"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tier Status"
                    value={userData?.tier || 'Basic'}
                    disabled
                    InputProps={{
                      endAdornment: (
                        <Button size="small" onClick={() => window.location.href = '#'}>
                          Upgrade
                        </Button>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveProfile}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setEditMode(false)}
                  disabled={!editMode}
                >
                  Cancel
                </Button>
              </Box>
            </TabPanel>

            {/* Security Tab */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.currentPassword}
                    onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowPassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={handleChangePassword}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              >
                {saving ? 'Updating...' : 'Update Password'}
              </Button>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" gutterBottom>
                Security Settings
              </Typography>
              
              <Box>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Two-Factor Authentication (2FA)"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Add an extra layer of security to your account
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={<Switch />}
                  label="Login notifications"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Get notified when someone logs into your account from a new device
                </Typography>
              </Box>
            </TabPanel>

            {/* Verification Tab */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>
                Identity Verification
              </Typography>

              {verificationError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setVerificationError(null)}>
                  {verificationError}
                </Alert>
              )}

              {verificationSuccess && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setVerificationSuccess(null)}>
                  {verificationSuccess}
                </Alert>
              )}

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VerifiedUser color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Verification Status</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getVerificationChip()}
                      </Typography>
                    </Box>
                  </Box>

                  {verificationStatus === 'not_started' && (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Verify your identity to access premium tasks and increase your earning potential.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => setVerificationDialogOpen(true)}
                        startIcon={<ArrowForward />}
                      >
                        Start Verification
                      </Button>
                    </>
                  )}

                  {verificationStatus === 'pending' && (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Your verification documents are under review. This usually takes 1-2 business days.
                      </Typography>
                      <LinearProgress sx={{ mb: 2 }} />
                      <Typography variant="caption" color="text.secondary">
                        Review in progress...
                      </Typography>
                    </>
                  )}

                  {verificationStatus === 'verified' && (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Your identity has been successfully verified! You now have access to:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Premium high-paying tasks" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Increased trust rating" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary="Priority support" />
                        </ListItem>
                      </List>
                    </>
                  )}

                  {verificationStatus === 'rejected' && (
                    <>
                      <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        Your verification was rejected. Please check the requirements and try again.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setVerificationDialogOpen(true)}
                        startIcon={<Upload />}
                      >
                        Resubmit Documents
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              <Typography variant="h6" gutterBottom>
                Why Verify?
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Star color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Access to premium tasks" 
                    secondary="Get exclusive access to higher-paying verification and photography tasks" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CreditCard color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Higher earning potential" 
                    secondary="Verified users earn 20-30% more on average" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Increased trust" 
                    secondary="Build credibility with task posters and get more assignments" 
                  />
                </ListItem>
              </List>
            </TabPanel>

            {/* Notifications Tab */}
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h6" gutterBottom>
                Notification Preferences
              </Typography>
              
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notifications.email}
                      onChange={(e) => handleInputChange('notifications.email', e.target.checked)}
                    />
                  }
                  label="Email notifications"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Receive important updates and account notifications via email
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notifications.push}
                      onChange={(e) => handleInputChange('notifications.push', e.target.checked)}
                    />
                  }
                  label="Push notifications"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Get instant alerts on your device for new tasks and messages
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notifications.taskAlerts}
                      onChange={(e) => handleInputChange('notifications.taskAlerts', e.target.checked)}
                    />
                  }
                  label="Task alerts"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Notify me about new tasks matching my preferences
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.notifications.promotional}
                      onChange={(e) => handleInputChange('notifications.promotional', e.target.checked)}
                    />
                  }
                  label="Promotional emails"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Receive offers, tips, and updates about PropertyEarn
                </Typography>
              </Box>

              <Button variant="contained" sx={{ mt: 3 }}>
                Save Preferences
              </Button>
            </TabPanel>

            {/* Payment Methods Tab */}
              <TabPanel value={tabValue} index={4}>
                <Typography variant="h6" gutterBottom>
                  Billing Information
                </Typography>
                
                {loadingBilling ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading billing information...</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={billingData.name || ''}
                        onChange={(e) => handleBillingInputChange('name', e.target.value)}
                        placeholder="John Doe"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={billingData.email || ''}
                        onChange={(e) => handleBillingInputChange('email', e.target.value)}
                        placeholder="john.doe@example.com"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        type="tel"
                        value={billingData.phone || ''}
                        onChange={(e) => handleBillingInputChange('phone', e.target.value)}
                        placeholder="+44 123 456 7890"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 1"
                        value={billingData.address_line1 || ''}
                        onChange={(e) => handleBillingInputChange('address_line1', e.target.value)}
                        placeholder="123 Main Street"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 2"
                        value={billingData.address_line2 || ''}
                        onChange={(e) => handleBillingInputChange('address_line2', e.target.value)}
                        placeholder="Apt 4B"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={billingData.city || ''}
                        onChange={(e) => handleBillingInputChange('city', e.target.value)}
                        placeholder="London"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="State/Province"
                        value={billingData.state || ''}
                        onChange={(e) => handleBillingInputChange('state', e.target.value)}
                        placeholder="England"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={billingData.postal_code || ''}
                        onChange={(e) => handleBillingInputChange('postal_code', e.target.value)}
                        placeholder="SW1A 1AA"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={billingData.country || ''}
                        onChange={(e) => handleBillingInputChange('country', e.target.value)}
                        placeholder="United Kingdom"
                      />
                    </Grid>
                  </Grid>
                )}

                <Button
                  variant="contained"
                  sx={{ mt: 3 }}
                  onClick={handleUpdateBilling}
                  disabled={savingBilling || loadingBilling}
                  startIcon={savingBilling ? <CircularProgress size={16} /> : <Save />}
                >
                  {savingBilling ? 'Updating...' : 'Update Billing Info'}
                </Button>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" gutterBottom>
                  Payment Methods
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  Manage your payout methods in the Earnings section. 
                  <Button 
                    sx={{ ml: 1 }} 
                    onClick={() => window.location.href = '/earnings'}
                    size="small"
                  >
                    Go to Earnings
                  </Button>
                </Alert>
              </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Verification Dialog */}
      <Dialog 
        open={verificationDialogOpen} 
        onClose={() => setVerificationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VerifiedUser sx={{ mr: 1 }} />
            Identity Verification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={verificationStep} orientation="vertical">
            <Step>
              <StepLabel>Government ID (Front)</StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload a clear photo of the front side of your government-issued ID
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                >
                  Upload Front of ID
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileUpload('idFront', e.target.files[0])}
                  />
                </Button>
                {verificationDocuments.idFront && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                    ✓ Uploaded
                  </Typography>
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Government ID (Back)</StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload a clear photo of the back side of your government-issued ID
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                >
                  Upload Back of ID
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileUpload('idBack', e.target.files[0])}
                  />
                </Button>
                {verificationDocuments.idBack && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                    ✓ Uploaded
                  </Typography>
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Selfie Verification</StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload a picture of you holding your ID next to your face 
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Camera />}
                >
                  Upload Picture
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handleFileUpload('selfie', e.target.files[0])}
                  />
                </Button>
                {verificationDocuments.selfie && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                    ✓ Uploaded
                  </Typography>
                )}
              </StepContent>
            </Step>
          </Stepper>

          {verificationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {verificationError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitVerification}
            disabled={uploading || !verificationDocuments.idFront || !verificationDocuments.idBack || !verificationDocuments.selfie}
            startIcon={uploading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {uploading ? 'Submitting...' : 'Submit Verification'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

